export function isEarned(l, today) {
    if (l.status === 'completed')
        return true;
    return l.status === 'confirmed' && l.day <= today;
}
export function isPlanned(l, today) {
    return l.status === 'confirmed' && l.day > today;
}
const inRange = (day, from, to) => day >= from && day <= to;
function emptySummary() {
    return { earnedGrosz: 0, plannedGrosz: 0, lessonsDone: 0, lessonsPlanned: 0 };
}
function add(into, l, today) {
    if (isEarned(l, today)) {
        into.earnedGrosz += l.priceGrosz;
        into.lessonsDone += 1;
    }
    else if (isPlanned(l, today)) {
        into.plannedGrosz += l.priceGrosz;
        into.lessonsPlanned += 1;
    }
}
/** Totals for every lesson whose day falls inside [from, to]. */
export function periodSummary(lessons, from, to, today) {
    const out = emptySummary();
    for (const l of lessons)
        if (inRange(l.day, from, to))
            add(out, l, today);
    return out;
}
/** The same, broken down per client. Lessons without a client are skipped. */
export function perClient(lessons, from, to, today) {
    const map = new Map();
    for (const l of lessons) {
        if (!l.clientId || !inRange(l.day, from, to))
            continue;
        let row = map.get(l.clientId);
        if (!row) {
            row = { clientId: l.clientId, ...emptySummary() };
            map.set(l.clientId, row);
        }
        add(row, l, today);
    }
    return [...map.values()].sort((a, b) => b.earnedGrosz - a.earnedGrosz);
}
/** All-time position of every client: used, paid, and the difference. */
export function clientBalances(clients, lessons, purchases, today) {
    const rows = new Map();
    for (const c of clients) {
        rows.set(c.id, {
            clientId: c.id,
            lessonsDone: 0,
            lessonsPlanned: 0,
            earnedGrosz: 0,
            plannedGrosz: 0,
            paidGrosz: 0,
            lessonsBought: 0,
            lessonsRemaining: 0,
            balanceGrosz: 0,
        });
    }
    for (const l of lessons) {
        const row = l.clientId ? rows.get(l.clientId) : undefined;
        if (!row)
            continue;
        if (isEarned(l, today)) {
            row.lessonsDone += 1;
            row.earnedGrosz += l.priceGrosz;
        }
        else if (isPlanned(l, today)) {
            row.lessonsPlanned += 1;
            row.plannedGrosz += l.priceGrosz;
        }
    }
    for (const p of purchases) {
        const row = rows.get(p.clientId);
        if (!row)
            continue;
        row.paidGrosz += p.totalGrosz;
        row.lessonsBought += p.lessons;
    }
    for (const row of rows.values()) {
        row.lessonsRemaining = row.lessonsBought - row.lessonsDone - row.lessonsPlanned;
        row.balanceGrosz = row.paidGrosz - row.earnedGrosz;
    }
    return [...rows.values()];
}
/** Twelve summaries, January first, for one year. */
export function monthlyTotals(lessons, year, today) {
    return Array.from({ length: 12 }, (_, m) => {
        const { from, to } = monthRange(year, m);
        return periodSummary(lessons, from, to, today);
    });
}
/**
 * Lesson `index` (0-based) of a purchase of `lessons` for `totalGrosz`, in
 * whole grosze. The shares differ by at most a grosz and always add up to
 * exactly what was paid: rounding each lesson on its own made 6 lessons for
 * 700 zł earn 700,02 zł, and a paid-up package showed „do zapłaty 0,02 zł”.
 */
export function packageLessonShare(totalGrosz, lessons, index) {
    const n = Math.max(1, lessons);
    const k = Math.min(Math.max(0, index), n - 1);
    return Math.floor((totalGrosz * (k + 1)) / n) - Math.floor((totalGrosz * k) / n);
}
/**
 * The price a new lesson for this client should carry: its share of the
 * oldest purchase that still has lessons left, else the client's usual rate.
 * `lessonsUsed` counts the client's lessons that already took a place (not
 * cancelled, not a pending request), so purchases are used up in order.
 */
export function suggestedLessonPrice(defaultPriceGrosz, purchases, lessonsUsed) {
    const sorted = [...purchases].sort((a, b) => (a.purchasedAt < b.purchasedAt ? -1 : 1));
    let remainingToConsume = lessonsUsed;
    for (const p of sorted) {
        if (remainingToConsume >= p.lessons) {
            remainingToConsume -= p.lessons;
            continue;
        }
        return packageLessonShare(p.totalGrosz, p.lessons, remainingToConsume);
    }
    return defaultPriceGrosz;
}
/* ── calendar-date helpers (pure string arithmetic, UTC under the hood) ── */
const pad = (n) => String(n).padStart(2, '0');
export function isoOfUTC(d) {
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}
export function addDays(iso, days) {
    const d = new Date(`${iso}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return isoOfUTC(d);
}
/** Monday of the week containing `iso`. */
export function startOfWeek(iso) {
    const d = new Date(`${iso}T00:00:00Z`);
    const lead = (d.getUTCDay() + 6) % 7;
    return addDays(iso, -lead);
}
/** Monday..Sunday containing `iso`. */
export function weekRange(iso) {
    const from = startOfWeek(iso);
    return { from, to: addDays(from, 6) };
}
/** `month` is 0-based, like Date. */
export function monthRange(year, month) {
    const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    return { from: `${year}-${pad(month + 1)}-01`, to: `${year}-${pad(month + 1)}-${pad(last)}` };
}
export function yearRange(year) {
    return { from: `${year}-01-01`, to: `${year}-12-31` };
}
